import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  LockKeyhole,
  Radio,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from 'lucide-react';
import bowLogo from '../logos/bow.svg';
import wobLogo from '../logos/wob.svg';
import { usePageContext } from '../contexts';

const CAMPAIGN_PASSWORD = 'panometra_client';

const CAMPAIGNS = {
  'multichoice-music': {
    name: 'Multichoice Music',
    eyebrow: 'Private campaign plan',
    title: 'Multichoice Music Release Growth Campaign',
    description:
      'A clear execution plan for launching new artistes, building audience momentum, and turning release data into the next set of commercial decisions.',
  },
};

const phases = [
  {
    label: 'Phase 01',
    title: 'Release readiness',
    summary: 'Make sure the campaign is ready before money, partners, and attention are committed.',
    items: [
      'Confirm rights, credits, splits, licenses, and sample clearance',
      'Prepare artwork, press shots, snippets, trailer, and behind-the-scenes assets',
      'Set up DSP upload, smart links, QA checks, and performance tracking',
      'Build press, radio, DJ, creator, and paid media activation lists',
    ],
  },
  {
    label: 'Phase 02',
    title: 'Launch week control',
    summary: 'Coordinate the release in real time so every partner, post, link, and follow-up is accounted for.',
    items: [
      'D-1: final link checks, captions, creator posts, and press timing',
      'D0: launch announcement, artiste introductions, smart links, and press release',
      'D1-D3: hook testing, DJ drops, playlist follow-up, and retargeting',
      'D4-D7: early performance review, fan reactions, and proof assets',
    ],
  },
  {
    label: 'Phase 03',
    title: 'Post-release growth',
    summary: 'Use the strongest signals from launch week to decide what should be scaled next.',
    items: [
      'Review streams, saves, follows, shares, comments, and city-level data',
      'Identify the strongest artiste, song, channel, and audience combinations',
      'Release interviews, visualisers, live clips, remixes, and fan stories',
      'Package the strongest artistes for brand, venue, festival, and press opportunities',
    ],
  },
];

const channels = [
  ['DSPs / playlists', 'Pitch editorial teams, follow up with independent curators, and support the songs showing early traction.', 'Streams, saves, playlist adds'],
  ['DJs / radio', 'Prepare clean edits, DJ packs, interview slots, and a station-by-station follow-up cadence.', 'Repetition, legitimacy, search lift'],
  ['Press / blogs', 'Shape the announcement, premieres, Q&A angles, and new-artiste stories that make the project searchable.', 'Credibility and narrative'],
  ['Creators / influencers', 'Brief creators around first-listen reactions, lifestyle usage, skits, challenges, and artiste introductions.', 'Social proof and UGC'],
  ['Paid media', 'Run controlled tests across Meta, YouTube, Snapchat, and X, then retarget the audiences that respond.', 'Reach and conversion'],
  ['IRL / live', 'Create a listening moment, showcase, performance capture, and community-facing release memory.', 'Content and community'],
];

const riskControls = [
  ['Late masters or metadata', 'Lock audio and metadata by T-6 so DSP pitching and partner outreach are not delayed.'],
  ['Unclear rights or credits', 'Confirm split sheets, sample clearance, licenses, and approvals before upload.'],
  ['Generic creator content', 'Give creators clear briefs with the artiste name, hook, CTA, usage rights, and posting window.'],
  ['Uncontrolled spend', 'Use approval gates before playlisting, radio, influencer, and paid media commitments.'],
  ['Weak early traction', 'Adjust by song, city, hook, creator group, or content format within the first 72 hours.'],
  ['Artistes lose visibility', 'Create mandatory spotlight assets and weekly performance notes for each featured artiste.'],
];

const budgetPhases = [
  ['Phase 01', 'Release readiness', '$4,140', 'Asset preparation, planning, campaign setup, tracking, partner lists, and launch QA.'],
  ['Phase 02', 'Launch week control', '$8,490', 'Creator coordination, press/radio follow-up, launch content, paid tests, and daily performance checks.'],
  ['Phase 03', 'Post-release growth', '$8,100', 'Retargeting, proof assets, interviews, live clips, fan reaction packaging, and next-step pitching.'],
];

const packageItems = [
  ['Strategy sprint', 'Define the positioning, audience map, release story, success metrics, budget envelope, and channel plan.'],
  ['Execution tracker', 'Create one shared view for tasks, owners, deadlines, vendors, costs, approvals, content, and KPIs.'],
  ['Campaign management', 'Run weekly check-ins, release-week coordination, creator/media/radio follow-up, and budget control.'],
  ['Growth report', 'Summarise artiste performance, audience insights, proof assets, and recommended next commercial moves.'],
];

const SectionKicker = ({ children }) => (
  <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.32em] text-orange-600">{children}</p>
);

const PasswordGate = ({ campaign, onUnlock }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    if (password === CAMPAIGN_PASSWORD) {
      window.sessionStorage.setItem('panometra-campaign-access', 'true');
      onUnlock();
      return;
    }
    setError('Incorrect password. Please check the access details shared with you.');
  };

  return (
    <div className="min-h-screen bg-[#fcfaf7] text-zinc-950">
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col px-6 py-8">
        <img src={bowLogo} alt="Panometra" className="h-7 w-fit" />
        <div className="grid flex-1 items-center gap-10 py-14 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <div className="mb-8 flex h-12 w-12 items-center justify-center border border-zinc-300 bg-white text-zinc-900">
              <LockKeyhole size={22} />
            </div>
            <SectionKicker>Private campaign room</SectionKicker>
            <h1 className="max-w-3xl text-5xl font-bold leading-[0.92] tracking-tight md:text-7xl">
              Access the {campaign.name} campaign plan.
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-zinc-600">
              This private page outlines the campaign structure, launch rhythm, channel plan, and delivery package prepared by Panometra.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="border border-zinc-200 bg-white p-6 shadow-sm md:p-8">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-zinc-400">Password required</p>
            <label htmlFor="campaign-password" className="mb-5 block text-2xl font-bold tracking-tight">
              Enter the shared campaign password
            </label>
            <input
              id="campaign-password"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setError('');
              }}
              className="h-14 w-full border border-zinc-300 bg-[#fcfaf7] px-4 text-lg outline-none transition focus:border-zinc-950"
              autoComplete="current-password"
              autoFocus
            />
            {error && <p className="mt-4 text-sm font-medium text-red-600">{error}</p>}
            <button className="mt-6 inline-flex h-12 w-full items-center justify-center gap-3 bg-zinc-950 px-5 text-xs font-bold uppercase tracking-[0.2em] text-white transition hover:bg-orange-600">
              Unlock campaign
              <ArrowRight size={16} />
            </button>
            <p className="mt-5 text-sm leading-relaxed text-zinc-500">
              This page is intended for the Multichoice Music campaign team and invited collaborators.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export const CampaignPage = () => {
  const { currentCampaignSlug } = usePageContext();
  const [hasAccess, setHasAccess] = useState(() => window.sessionStorage.getItem('panometra-campaign-access') === 'true');

  const campaign = CAMPAIGNS[currentCampaignSlug];

  const navItems = useMemo(
    () => [
      ['Mandate', '#mandate'],
      ['Runway', '#runway'],
      ['Phases', '#phases'],
      ['Channels', '#channels'],
      ['Governance', '#governance'],
    ],
    []
  );

  if (!campaign) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fcfaf7] px-6 text-center text-zinc-950">
        <div>
          <img src={bowLogo} alt="Panometra" className="mx-auto mb-10 h-7" />
          <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.32em] text-zinc-400">Campaign not found</p>
          <h1 className="text-4xl font-bold tracking-tight">This private campaign page is unavailable.</h1>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return <PasswordGate campaign={campaign} onUnlock={() => setHasAccess(true)} />;
  }

  return (
    <div className="bg-[#fcfaf7] text-zinc-950">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-zinc-200 bg-[#fcfaf7]/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:h-20">
          <a href="#top" className="flex items-center">
            <img src={bowLogo} alt="Panometra" className="h-6 w-auto sm:h-8" />
          </a>
          <nav className="hidden items-center gap-8 md:flex">
            {navItems.map(([label, href]) => (
              <a key={label} href={href} className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 transition hover:text-zinc-950">
                {label}
              </a>
            ))}
          </nav>
          <a href="#package" className="inline-flex h-10 items-center gap-2 bg-zinc-950 px-4 text-[10px] font-bold uppercase tracking-[0.18em] text-white transition hover:bg-orange-600">
            Delivery
            <ChevronRight size={14} />
          </a>
        </div>
      </header>

      <main id="top">
        <section className="relative min-h-[760px] overflow-hidden bg-zinc-950 pt-28 text-white md:pt-36">
          <img
            src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&q=85"
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-42 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-zinc-950/30" />
          <div className="relative z-10 mx-auto grid max-w-7xl items-end gap-10 px-6 pb-14 md:min-h-[620px] md:grid-cols-[1.1fr_0.9fr] md:pb-20">
            <div>
              <SectionKicker>{campaign.eyebrow}</SectionKicker>
              <p className="mb-5 text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-400">{campaign.name}</p>
              <h1 className="max-w-5xl text-5xl font-bold leading-[0.88] tracking-tight md:text-8xl">
                {campaign.title}
              </h1>
              <p className="mt-8 max-w-3xl text-xl leading-relaxed text-zinc-300 md:text-2xl">
                {campaign.description}
              </p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <a href="#runway" className="inline-flex items-center justify-center gap-3 bg-white px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-950 transition hover:bg-orange-500 hover:text-white">
                  View campaign runway
                  <ArrowRight size={16} />
                </a>
                <a href="#package" className="inline-flex items-center justify-center gap-3 border border-white/25 px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition hover:border-white hover:bg-white/10">
                  See delivery package
                </a>
              </div>
            </div>
            <aside className="border border-white/15 bg-white/10 p-6 backdrop-blur md:p-8">
              <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.28em] text-orange-300">Campaign principle</p>
              <h2 className="text-3xl font-bold leading-tight tracking-tight">
                Every activity should help the project get ready, reach the right audience, or extend momentum after launch.
              </h2>
              <div className="mt-8 grid grid-cols-3 gap-px bg-white/15">
                {[
                  ['12', 'week runway'],
                  ['3', 'campaign phases'],
                  ['1', 'live tracker'],
                ].map(([metric, label]) => (
                  <div key={label} className="bg-zinc-950/70 p-4">
                    <strong className="block text-3xl">{metric}</strong>
                    <span className="mt-1 block text-xs uppercase tracking-[0.16em] text-zinc-400">{label}</span>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </section>

        <section id="mandate" className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-[0.75fr_1.25fr] md:py-28">
          <div>
            <SectionKicker>Mandate</SectionKicker>
            <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">
              Treat the release as a managed campaign, not a burst of promotion.
            </h2>
          </div>
          <div>
            <p className="text-xl leading-relaxed text-zinc-600 md:text-2xl">
              The goal is to introduce the artistes clearly, drive measurable audience action, and create enough proof for Multichoice Music to decide where to invest next.
            </p>
            <div className="mt-10 grid gap-px bg-zinc-200 md:grid-cols-3">
              {[
                ['01', 'Find the audience', 'Identify the platforms, communities, curators, creators, and cities most likely to respond early.'],
                ['02', 'Launch the project', 'Move the release into streams, saves, playlist adds, press, creator content, radio/DJ support, and live attention.'],
                ['03', 'Back what works', 'Use real market signals to decide which artistes, songs, cities, hooks, and partners deserve more support.'],
              ].map(([number, title, body]) => (
                <article key={title} className="bg-white p-6">
                  <span className="text-[10px] font-bold uppercase tracking-[0.26em] text-orange-600">{number}</span>
                  <h3 className="mt-8 text-2xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-4 leading-relaxed text-zinc-600">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-zinc-950 text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 md:grid-cols-[0.9fr_1.1fr] md:py-28">
            <div>
              <SectionKicker>Definition of success</SectionKicker>
              <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">
                Create a visible campaign moment while making each artiste easy to remember.
              </h2>
            </div>
            <div className="grid gap-px bg-white/10">
              {[
                ['Commercial objective', 'Generate enough demand signals to guide spend, bookings, brand interest, and artiste development decisions.'],
                ['Cultural objective', 'Make the release feel like a moment, not just an upload, by connecting the music to story, people, place, and social behaviour.'],
                ['Artiste objective', 'Give every featured artiste a clear public identity, a recognisable hook, and a measurable fan funnel.'],
              ].map(([title, body]) => (
                <article key={title} className="bg-zinc-950 p-6">
                  <h3 className="text-xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-3 leading-relaxed text-zinc-400">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="runway" className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <div className="mb-12 max-w-3xl">
            <SectionKicker>12-week campaign runway</SectionKicker>
            <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">From setup to launch proof.</h2>
            <p className="mt-5 text-xl leading-relaxed text-zinc-600">
              Dates can shift around the final release day. The workflow stays the same: prepare early, launch with control, then scale what performs.
            </p>
          </div>
          <div className="overflow-hidden border border-zinc-200 bg-white">
            <div
              className="grid min-w-[980px] border-b border-zinc-200 bg-zinc-950 text-white"
              style={{ gridTemplateColumns: 'repeat(13, minmax(0, 1fr))' }}
            >
              {['T-8', 'T-7', 'T-6', 'T-5', 'T-4', 'T-3', 'T-2', 'T-1', 'REL', 'T+1', 'T+2', 'T+4', 'T+8'].map((week) => (
                <span key={week} className="border-r border-white/10 px-3 py-4 text-center text-[10px] font-bold uppercase tracking-[0.16em] last:border-r-0">{week}</span>
              ))}
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[980px] p-5">
                {[
                  ['Strategy', 'Story, targets, budget, roles', '1 / span 4', ''],
                  ['Assets', 'Masters, artwork, EPK, artiste intros, BTS', '2 / span 5', ''],
                  ['Seeding', 'DSP pitch, DJs, creators, press, pre-save', '4 / span 5', ''],
                  ['Launch room', 'Daily content, ads, creators, press, live moment', '8 / span 3', 'bg-orange-100 border-orange-300'],
                  ['Growth loop', 'Proof assets, retargeting, interviews, live bookings', '10 / span 4', ''],
                ].map(([title, body, gridColumn, emphasis]) => (
                  <div
                    key={title}
                    className="mb-3 grid last:mb-0"
                    style={{ gridTemplateColumns: 'repeat(13, minmax(0, 1fr))' }}
                  >
                    <div className={`${emphasis || 'bg-[#fcfaf7]'} border border-zinc-200 px-4 py-4`} style={{ gridColumn }}>
                      <b className="block text-sm uppercase tracking-[0.18em]">{title}</b>
                      <em className="mt-1 block text-sm not-italic text-zinc-500">{body}</em>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 grid gap-px bg-zinc-200 md:grid-cols-4">
            {['Gate 1: Assets approved', 'Gate 2: Partners confirmed', 'Gate 3: Launch ready', 'Gate 4: Scale / pause decisions'].map((gate) => (
              <div key={gate} className="bg-white p-4 text-sm font-bold uppercase tracking-[0.14em] text-zinc-700">{gate}</div>
            ))}
          </div>
        </section>

        <section id="phases" className="border-t border-zinc-200 bg-white">
          <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
            <div className="mb-12 max-w-4xl">
              <SectionKicker>Campaign phases</SectionKicker>
              <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">
                Prepare the release, control launch week, then grow from the strongest signals.
              </h2>
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              {phases.map((phase) => (
                <article key={phase.title} className="border border-zinc-200 bg-[#fcfaf7] p-6 md:p-8">
                  <span className="text-[10px] font-bold uppercase tracking-[0.26em] text-orange-600">{phase.label}</span>
                  <h3 className="mt-7 text-3xl font-bold leading-tight tracking-tight">{phase.title}</h3>
                  <p className="mt-4 leading-relaxed text-zinc-600">{phase.summary}</p>
                  <ul className="mt-8 space-y-4">
                    {phase.items.map((item) => (
                      <li key={item} className="flex gap-3 text-sm leading-relaxed text-zinc-700">
                        <CheckCircle2 size={16} className="mt-1 shrink-0 text-zinc-950" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="channels" className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <div className="mb-12 max-w-4xl">
            <SectionKicker>Channel activation matrix</SectionKicker>
            <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">Each channel has a clear role in the campaign.</h2>
            <p className="mt-5 text-xl leading-relaxed text-zinc-600">
              Spend should move toward the channel, artiste, song, and city combination showing the strongest saves, shares, comments, follows, and repeat engagement.
            </p>
          </div>
          <div className="grid gap-px bg-zinc-200 md:grid-cols-2 lg:grid-cols-3">
            {channels.map(([title, body, metric], index) => {
              const icons = [Sparkles, Radio, ClipboardCheck, UsersRound, BarChart3, CalendarDays];
              const Icon = icons[index];
              return (
                <article key={title} className="bg-white p-6">
                  <div className="mb-10 flex h-11 w-11 items-center justify-center border border-zinc-200 text-zinc-900">
                    <Icon size={19} />
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-4 leading-relaxed text-zinc-600">{body}</p>
                  <strong className="mt-6 block text-xs uppercase tracking-[0.18em] text-orange-700">{metric}</strong>
                </article>
              );
            })}
          </div>
        </section>

        <section className="bg-zinc-950 text-white">
          <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-[0.9fr_1.1fr] md:py-28">
            <div>
              <SectionKicker>Budget model</SectionKicker>
              <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">Estimate spend by campaign phase.</h2>
              <p className="mt-6 text-xl leading-relaxed text-zinc-400">
                The working budget is structured around the three parts of the campaign: readiness, launch control, and post-release growth. Each phase can be approved before spend is committed.
              </p>
            </div>
            <div className="grid gap-4">
              <article className="border border-white/10 bg-white/5 p-6 md:p-8">
                <CircleDollarSign className="mb-8 text-orange-300" size={28} />
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-500">Estimated campaign budget</p>
                <h3 className="mt-4 text-4xl font-bold tracking-tight">$20,730 total</h3>
                <p className="mt-4 max-w-2xl leading-relaxed text-zinc-400">
                  This is a planning estimate for the campaign execution model. Final spend should be confirmed after release dates, vendor quotes, creator scope, and media commitments are locked.
                </p>
              </article>
              <div className="grid gap-px bg-white/10">
                {budgetPhases.map(([phase, title, amount, body]) => (
                  <article key={phase} className="grid gap-4 bg-zinc-950 p-5 md:grid-cols-[110px_1fr_120px] md:items-start">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-orange-300">{phase}</p>
                    <div>
                      <h3 className="text-2xl font-bold tracking-tight">{title}</h3>
                      <p className="mt-2 leading-relaxed text-zinc-400">{body}</p>
                    </div>
                    <strong className="text-2xl font-bold tracking-tight text-white md:text-right">{amount}</strong>
                  </article>
                ))}
              </div>
              <article className="border border-white/10 bg-white/5 p-6 md:p-8">
                <ShieldCheck className="mb-8 text-orange-300" size={28} />
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-500">Spend control</p>
                <h3 className="mt-4 text-4xl font-bold tracking-tight">Approve each phase before activation</h3>
                <p className="mt-6 leading-relaxed text-zinc-400">
                  Panometra will separate planned, approved, committed, and actual spend so Multichoice Music can see what has been authorised and what remains optional.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section id="governance" className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-[0.8fr_1.2fr] md:py-28">
          <div>
            <SectionKicker>Governance</SectionKicker>
            <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">One shared tracker for decisions, owners, spend, and progress.</h2>
          </div>
          <div className="grid gap-6">
            <div className="border border-zinc-200 bg-white">
              {[
                ['Work plan', 'Task title, workstream, dependency, deliverable, priority'],
                ['Timing', 'Planned start, deadline, duration, days left, blocker status'],
                ['Ownership', 'Panometra lead, Multichoice Music owner, vendor or handler, approval owner'],
                ['Budget', 'Projected cost, approved budget, committed spend, actual cost, invoice status'],
                ['Performance', 'Progress %, KPI target, actual KPI, scale or pause recommendation'],
              ].map(([title, body]) => (
                <div key={title} className="grid gap-2 border-b border-zinc-200 p-5 last:border-b-0 md:grid-cols-[220px_1fr]">
                  <strong>{title}</strong>
                  <span className="text-zinc-600">{body}</span>
                </div>
              ))}
            </div>
            <div className="border border-zinc-200 bg-white p-6">
              <h3 className="mb-5 text-2xl font-bold tracking-tight">Meeting cadence</h3>
              <div className="grid gap-px bg-zinc-200 md:grid-cols-2">
                {['Monday: priorities, blockers, owner check', 'Wednesday: partner follow-up and content QA', 'Friday: KPI readout and spend decision', 'Release week: daily 20-minute launch room'].map((item) => (
                  <p key={item} className="bg-[#fcfaf7] p-4 text-sm leading-relaxed text-zinc-700">{item}</p>
                ))}
              </div>
              <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-orange-700">Decision language: Continue / Scale / Pause / Stop.</p>
            </div>
          </div>
        </section>

        <section className="border-y border-zinc-200 bg-white">
          <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
            <div className="mb-12 max-w-4xl">
              <SectionKicker>Risk controls</SectionKicker>
              <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">Protect the budget, timeline, brand, and artiste visibility.</h2>
            </div>
            <div className="grid gap-px bg-zinc-200 md:grid-cols-2 lg:grid-cols-3">
              {riskControls.map(([title, body]) => (
                <article key={title} className="bg-[#fcfaf7] p-6">
                  <h3 className="text-xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-4 leading-relaxed text-zinc-600">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="package" className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-[0.85fr_1.15fr] md:py-28">
          <div>
            <SectionKicker>Panometra delivery package</SectionKicker>
            <h2 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">
              A campaign delivery package for Multichoice Music, managed from strategy through post-release growth.
            </h2>
          </div>
          <div className="grid gap-px bg-zinc-200">
            {packageItems.map(([title, body], index) => (
              <article key={title} className="grid gap-5 bg-white p-6 md:grid-cols-[64px_1fr]">
                <span className="flex h-12 w-12 items-center justify-center border border-zinc-200 text-sm font-bold text-orange-700">{index + 1}</span>
                <div>
                  <h3 className="text-2xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-3 leading-relaxed text-zinc-600">{body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="bg-zinc-950 px-6 py-8 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <img src={wobLogo} alt="Panometra" className="h-6 w-fit opacity-80" />
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">{campaign.name} · Private campaign page · Panometra © 2026</p>
        </div>
      </footer>
    </div>
  );
};
